import { Box, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LiveDot } from '../../../shared/components/LiveDot';
import { Pill } from '../../../shared/components/Pill';
import type { AiAssistantRole } from '../../../core/types/data-models';
import { roleIcon } from './assistantRoles';

/**
 * Icon tile, status pill, name and description — shared by both AI Assistants
 * layouts. Tinted entirely from the role's own `color`/`avatarIcon`
 * (backend-assigned per role) rather than a hashed category color, so each
 * assistant reads as a consistent, distinct identity across the page.
 * `hasDocuments` drives the Active/Standby status pill: there's no live
 * "is generating" signal from the backend to key off, so this uses the one
 * honest proxy available — whether the role has produced anything yet —
 * rather than fabricating a busier-looking state.
 */
export function AssistantHeaderBlock({
  role,
  hasDocuments,
  docCount,
  sx,
}: {
  role: AiAssistantRole;
  hasDocuments: boolean;
  docCount: number;
  sx?: SxProps<Theme>;
}) {
  // alpha() needs a concrete color, not a CSS custom property, so the
  // fallback (a role with no color assigned) is a plain hex rather than
  // `var(--color-primary)`.
  const roleColor = role.color ?? '#6366f1';
  const RoleIcon = roleIcon(role.avatarIcon);
  return (
    <Stack
      spacing={1.25}
      sx={[
        {
          p: 2,
          height: '100%',
          backgroundColor: alpha(roleColor, 0.12),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
            color: roleColor,
            backgroundColor: alpha(roleColor, 0.18),
            border: '1px solid',
            borderColor: alpha(roleColor, 0.4),
          }}
        >
          <RoleIcon sx={{ fontSize: 20 }} />
        </Box>
        <Pill color={hasDocuments ? 'success' : 'neutral'} sx={{ flexShrink: 0 }}>
          {hasDocuments && <LiveDot size={5} />}
          {hasDocuments ? 'Active' : 'Standby'}
        </Pill>
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} lineHeight={1.25}>
          {role.name}
        </Typography>
        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: roleColor, mt: 0.25 }}>
          {docCount} Generated Artifact{docCount === 1 ? '' : 's'}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            mt: 0.5,
          }}
        >
          {role.description}
        </Typography>
      </Box>
    </Stack>
  );
}
